import React from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  PRODUCT_COMPARTMENT_FILL_MATCH_MODE_OPTIONS,
  PRODUCT_COMPARTMENT_FILL_MAX_RULES,
  buildCatalogPreviewLabel,
  buildFillExampleLine,
  createEmptyFillRule,
  resolveFillPercentForCode,
  validateFillRules,
} from "../lib/productCompartmentFillRules.js";
import { buttonToneClasses, joinClasses } from "./resultsPrimitives/shared.js";

const FIELD_CLASS_NAME = "h-6 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-1.5 py-0 text-[0.76rem] font-medium text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";
const FIELD_ERROR_CLASS_NAME = "border-[var(--color-border-danger-subtle)] bg-danger-soft";
const ACTION_CLASS_NAME = "inline-flex h-6 items-center justify-center gap-1 rounded-[4px] border px-2 py-0 text-[0.72rem] font-semibold transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-border-accent-subtle disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const DELETE_CLASS_NAME = "inline-flex h-6 w-6 items-center justify-center rounded-[4px] border border-slate-200 bg-white p-0 text-danger transition hover:border-[var(--color-border-danger-subtle)] hover:bg-danger-soft focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-border-accent-subtle disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400";

function RuleErrors({ errors }) {
  const messages = [errors?.pattern, errors?.fill_percent, errors?.duplicate].filter(Boolean);
  if (!messages.length) {
    return null;
  }
  return (
    <p className="m-0 text-[0.68rem] font-semibold leading-4 text-danger">{messages.join(" ")}</p>
  );
}

export default function ProductCompartmentFillRulesControl({ canEditSetting, onChange, setting, value }) {
  const rules = Array.isArray(value) ? value : [];
  const productCodeOptions = Array.isArray(setting?.product_code_options) ? setting.product_code_options : [];
  const errorsByRow = validateFillRules(rules);
  const examplePercents = Array.from(new Set(
    rules
      .map((rule) => resolveFillPercentForCode(rule?.pattern, [rule]))
      .filter((percent) => percent !== null),
  )).sort((left, right) => right - left);

  const updateRule = (index, patch) => {
    onChange(setting.key, rules.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, ...patch } : rule)));
  };
  const removeRule = (index) => {
    onChange(setting.key, rules.filter((_rule, ruleIndex) => ruleIndex !== index));
  };
  const addRule = () => {
    onChange(setting.key, [...rules, createEmptyFillRule()]);
  };

  return (
    <div className="grid min-w-0 gap-1.5">
      {rules.length ? (
        <div className="min-w-0 overflow-x-auto rounded-[4px] border border-slate-200 bg-white">
          <table className="w-full min-w-0 border-collapse text-left text-[0.76rem]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[0.66rem] font-bold uppercase tracking-[0.08em] text-slate-500">
                <th className="px-1.5 py-1 font-bold" scope="col">Condition</th>
                <th className="px-1.5 py-1 font-bold" scope="col">Code produit</th>
                <th className="px-1.5 py-1 font-bold" scope="col">Remplissage max %</th>
                <th className="px-1.5 py-1 font-bold" scope="col"><span className="sr-only">Supprimer</span></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, index) => {
                const rowErrors = errorsByRow[index] || {};
                const previewLabel = buildCatalogPreviewLabel(rule, productCodeOptions);
                return (
                  <React.Fragment key={index}>
                    <tr className="align-top">
                      <td className="px-1.5 pt-1">
                        <select
                          aria-label={`Condition de la règle ${index + 1}`}
                          className={joinClasses([FIELD_CLASS_NAME, "min-w-[7.5rem]"])}
                          disabled={!canEditSetting}
                          onChange={(event) => updateRule(index, { match_mode: event.target.value })}
                          value={rule.match_mode}
                        >
                          {PRODUCT_COMPARTMENT_FILL_MATCH_MODE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1.5 pt-1">
                        <input
                          aria-label={`Code produit de la règle ${index + 1}`}
                          className={joinClasses([FIELD_CLASS_NAME, "min-w-[5.5rem]", rowErrors.pattern || rowErrors.duplicate ? FIELD_ERROR_CLASS_NAME : ""])}
                          disabled={!canEditSetting}
                          maxLength={40}
                          onChange={(event) => updateRule(index, { pattern: event.target.value })}
                          placeholder="172"
                          type="text"
                          value={rule.pattern}
                        />
                      </td>
                      <td className="px-1.5 pt-1">
                        <div className="flex items-center gap-1">
                          <input
                            aria-label={`Remplissage maximal de la règle ${index + 1}`}
                            className={joinClasses([FIELD_CLASS_NAME, "w-16", rowErrors.fill_percent ? FIELD_ERROR_CLASS_NAME : ""])}
                            disabled={!canEditSetting}
                            inputMode="numeric"
                            max={100}
                            min={1}
                            onChange={(event) => updateRule(index, { fill_percent: event.target.value })}
                            step={1}
                            type="number"
                            value={rule.fill_percent}
                          />
                          <span className="text-[0.72rem] font-semibold text-slate-500">%</span>
                        </div>
                      </td>
                      <td className="px-1.5 pt-1 text-right">
                        <button
                          aria-label={`Supprimer la règle ${index + 1}`}
                          className={DELETE_CLASS_NAME}
                          disabled={!canEditSetting}
                          onClick={() => removeRule(index)}
                          title="Supprimer la règle"
                          type="button"
                        >
                          <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                    <tr className="border-b border-slate-100 last:border-b-0">
                      <td className="px-1.5 pb-1 pt-0.5" colSpan={4}>
                        <RuleErrors errors={rowErrors} />
                        {previewLabel ? (
                          <p className="m-0 text-[0.68rem] leading-4 text-slate-500 wrap-break-word">{previewLabel}</p>
                        ) : null}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="m-0 rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[0.76rem] leading-5 text-slate-500">
          Aucune règle : tous les produits remplissent les compartiments jusqu’à leur capacité.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          className={joinClasses([ACTION_CLASS_NAME, buttonToneClasses.ghost])}
          disabled={!canEditSetting || rules.length >= PRODUCT_COMPARTMENT_FILL_MAX_RULES}
          onClick={addRule}
          type="button"
        >
          <Plus aria-hidden="true" className="h-3.5 w-3.5" />
          Ajouter une règle
        </button>
      </div>

      {examplePercents.length ? (
        <div className="grid gap-0.5 rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-1">
          {examplePercents.map((percent) => (
            <p key={percent} className="m-0 text-[0.68rem] leading-4 text-slate-600 wrap-break-word">
              <span className="font-semibold text-slate-700">{percent} % :</span> {buildFillExampleLine(percent)}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
